import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
    Chip,
} from '@mui/material';
import {
    getShopLayoutSettings,
    getShopProducts,
    updateShopLayoutSettings,
    updateShopProduct,
} from '../Api';
import { useNavigate } from 'react-router-dom';
import ShopItemCard from '../components/ShopItemCard';
import ShopLayoutPreview, { ShopLayoutItemPatch } from '../components/ShopLayoutPreview';
import ShopItemDto from '../models/dtos/ShopItemDto';
import { toShopItemPayload } from '../utils/shopItemPayload';
import {
    DEFAULT_FREE_REWARDS_DISPLAY_ORDER,
    DEFAULT_SECTION_DISPLAY_ORDER,
    normalizeSectionDisplayOrder,
} from '../utils/shopLayoutPreview';
import { ShopDisplaySection } from '../models/dtos/ShopItemDto';

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
}

export default function ShopPage() {
    const [state, setState] = React.useState({
        itemsLoaded: false,
        osTypes: ['All', 'iOS', 'Android'],
        osType: 'All',
        items: [] as ShopItemDto[],
    });
    const [statusFilter, setStatusFilter] = React.useState<'active' | 'disabled' | 'all'>('active');
    const [settingsLoading, setSettingsLoading] = React.useState(true);
    const [settingsSaving, setSettingsSaving] = React.useState(false);
    const [shopV2Enabled, setShopV2Enabled] = React.useState(false);
    const [freeRewardsDisplayOrder, setFreeRewardsDisplayOrder] = React.useState(
        DEFAULT_FREE_REWARDS_DISPLAY_ORDER,
    );
    const [savedFreeRewardsDisplayOrder, setSavedFreeRewardsDisplayOrder] = React.useState(
        DEFAULT_FREE_REWARDS_DISPLAY_ORDER,
    );
    const [sectionDisplayOrder, setSectionDisplayOrder] = React.useState<ShopDisplaySection[]>(
        [...DEFAULT_SECTION_DISPLAY_ORDER],
    );
    const [savedSectionDisplayOrder, setSavedSectionDisplayOrder] = React.useState<ShopDisplaySection[]>(
        [...DEFAULT_SECTION_DISPLAY_ORDER],
    );
    const [settingsMessage, setSettingsMessage] = React.useState<string | null>(null);
    const [dirtyItemIds, setDirtyItemIds] = React.useState<Set<number>>(new Set());
    const [layoutSaving, setLayoutSaving] = React.useState(false);

    const navigate = useNavigate();

    const fetchData = async (os: string) => {
        const osType = os.toLowerCase() == 'all' ? undefined : os.toLowerCase();
        let items = await getShopProducts(osType);
        setState(prevState => ({ ...prevState, items: items ?? [], itemsLoaded: true }));
        setDirtyItemIds(new Set());
    }

    const onOsTypeChanged = (osType: string) =>{
        setState(prevState => ({...prevState, osType: osType, itemsLoaded: false}));
        fetchData(osType);
    }

    React.useEffect(() => {
        fetchData(state.osType);
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setSettingsLoading(true);
            const dto = await getShopLayoutSettings();
            if (!cancelled) {
                const order = dto?.freeRewardsDisplayOrder ?? DEFAULT_FREE_REWARDS_DISPLAY_ORDER;
                const sections = normalizeSectionDisplayOrder(dto?.sectionDisplayOrder);
                setShopV2Enabled(dto?.shopV2LayoutEnabled ?? false);
                setFreeRewardsDisplayOrder(order);
                setSavedFreeRewardsDisplayOrder(order);
                setSectionDisplayOrder(sections);
                setSavedSectionDisplayOrder(sections);
                setSettingsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const onSaveLayoutSettings = async () => {
        setSettingsSaving(true);
        setSettingsMessage(null);
        const ok = await updateShopLayoutSettings({
            shopV2LayoutEnabled: shopV2Enabled,
            freeRewardsDisplayOrder,
            sectionDisplayOrder,
        });
        setSettingsSaving(false);
        if (!ok) {
            setSettingsMessage('Failed to save shop layout settings. Check server logs and API connection.');
            return;
        }
        const dto = await getShopLayoutSettings();
        const order = dto?.freeRewardsDisplayOrder ?? freeRewardsDisplayOrder;
        const sections = normalizeSectionDisplayOrder(dto?.sectionDisplayOrder ?? sectionDisplayOrder);
        setShopV2Enabled(dto?.shopV2LayoutEnabled ?? shopV2Enabled);
        setFreeRewardsDisplayOrder(order);
        setSavedFreeRewardsDisplayOrder(order);
        setSectionDisplayOrder(sections);
        setSavedSectionDisplayOrder(sections);
        setSettingsMessage(
            'Saved shop layout settings. Android picks this up on the next shop load '
            + '(section order, video placement, shop v2 flag).',
        );
    };

    const onItemPatch = (id: number, patch: ShopLayoutItemPatch) => {
        setState((prev) => ({
            ...prev,
            items: prev.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
        }));
        setDirtyItemIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    };

    const onSaveItem = async (item: ShopItemDto): Promise<boolean> => {
        if (!item.storeCode) return false;
        setLayoutSaving(true);
        const ok = await updateShopProduct(item.id, toShopItemPayload(item));
        setLayoutSaving(false);
        if (ok) {
            setDirtyItemIds((prev) => {
                const next = new Set(prev);
                next.delete(item.id);
                return next;
            });
        }
        return ok;
    };

    const onSaveFreeRewardsOrder = async (): Promise<boolean> => {
        setLayoutSaving(true);
        const ok = await updateShopLayoutSettings({
            shopV2LayoutEnabled: shopV2Enabled,
            freeRewardsDisplayOrder,
            sectionDisplayOrder,
        });
        setLayoutSaving(false);
        if (ok) {
            setSavedFreeRewardsDisplayOrder(freeRewardsDisplayOrder);
            setSavedSectionDisplayOrder(sectionDisplayOrder);
        }
        return ok;
    };

    const freeRewardsOrderDirty = freeRewardsDisplayOrder !== savedFreeRewardsDisplayOrder;
    const sectionOrderDirty =
        sectionDisplayOrder.join(',') !== savedSectionDisplayOrder.join(',');

    const activeCount = state.items.filter((i) => !i.isDeleted).length;
    const disabledCount = state.items.filter((i) => i.isDeleted).length;
    const filteredItems = state.items.filter((item) => {
        if (statusFilter === 'active') return !item.isDeleted;
        if (statusFilter === 'disabled') return item.isDeleted;
        return true;
    });

    return (
        <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
            <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 5 }} justifyContent="flex-end" width="100%">
                <Grid item xs={1} key="addButton" alignContent="center">
                    <Button variant="contained" fullWidth={true} style={{ height: '54px' }} onClick={() => navigate('/shop-items/create')}>Add shop item</Button>
                </Grid>
            </Grid>

            <Card sx={{ width: '100%', maxWidth: 1100 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Shop layout (Android)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Controls Android UI. When enabled: sectioned shop + <code>new_shop_*</code> catalog (HTML).
                        Reorder <strong>categories</strong> and items in the layout workspace below —
                        Save layout settings (or Save all) so the app picks up the new order on next shop load.
                        Older apps keep video at the bottom even if Free Rewards moves in the category list.
                    </Typography>
                    {settingsLoading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={shopV2Enabled}
                                            onChange={(e) => setShopV2Enabled(e.target.checked)}
                                            color="warning"
                                        />
                                    }
                                    label={shopV2Enabled ? 'Shop v2 enabled' : 'Shop v2 disabled (legacy grid)'}
                                />
                                <TextField
                                    type="number"
                                    label="Free Rewards / video order"
                                    size="small"
                                    sx={{ width: 220 }}
                                    helperText="Lower = higher in list (e.g. after First Purchase)"
                                    value={freeRewardsDisplayOrder}
                                    onChange={(e) =>
                                        setFreeRewardsDisplayOrder(parseInt(e.target.value, 10) || 0)
                                    }
                                />
                                <Button
                                    variant="contained"
                                    onClick={onSaveLayoutSettings}
                                    disabled={settingsSaving}
                                >
                                    {settingsSaving ? 'Saving…' : 'Save layout settings'}
                                </Button>
                            </Stack>
                        </Stack>
                    )}
                    {settingsMessage ? (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity={settingsMessage.startsWith('Failed') ? 'error' : 'success'}>
                                {settingsMessage}
                            </Alert>
                        </Box>
                    ) : null}
                </CardContent>
            </Card>

            <Card sx={{ width: '100%', maxWidth: 1100 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Shop layout workspace</Typography>
                    {!state.itemsLoaded ? (
                        <CircularProgress size={24} />
                    ) : (
                        <ShopLayoutPreview
                            items={state.items}
                            freeRewardsDisplayOrder={freeRewardsDisplayOrder}
                            onFreeRewardsDisplayOrderChange={setFreeRewardsDisplayOrder}
                            sectionDisplayOrder={sectionDisplayOrder}
                            onSectionDisplayOrderChange={setSectionDisplayOrder}
                            onItemPatch={onItemPatch}
                            onSaveItem={onSaveItem}
                            onSaveFreeRewardsOrder={onSaveFreeRewardsOrder}
                            dirtyItemIds={dirtyItemIds}
                            freeRewardsOrderDirty={freeRewardsOrderDirty || sectionOrderDirty}
                            saving={layoutSaving}
                        />
                    )}
                </CardContent>
            </Card>

            <Typography variant="h3">Shop Items</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} width="100%">
                <Chip label={`${activeCount} active`} color="success" variant="outlined" size="small" />
                <Chip label={`${disabledCount} disabled`} color={disabledCount > 0 ? 'warning' : 'default'} variant="outlined" size="small" />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    Disabled items are hidden in the app after API deploy; they stay visible here for audit.
                    Prefer the layout workspace above for order / row width; open Full editor for HTML and rewards.
                </Typography>
            </Stack>
            <Grid container spacing={5} columns={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 4 }} width="100%">
                <Grid item sm={5} key="osselect">
                    <FormControl fullWidth>
                        <InputLabel id="demo-location-select-label">Operating System</InputLabel>
                        <Select
                            labelId="banner-simple-select-label"
                            id="location-simple-select"
                            value={state.osType}
                            onChange={e => onOsTypeChanged(e.target.value)}
                            label="Type">
                            {
                                state.osTypes.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)
                            }
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={5} key="statusselect">
                    <FormControl fullWidth>
                        <InputLabel id="shop-status-filter-label">Status</InputLabel>
                        <Select
                            labelId="shop-status-filter-label"
                            id="shop-status-filter"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value as 'active' | 'disabled' | 'all')}
                            label="Status">
                            <MenuItem value="active">Active only</MenuItem>
                            <MenuItem value="disabled">Disabled only</MenuItem>
                            <MenuItem value="all">All items</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            {!state.itemsLoaded ? <Grid item xs={2}><CircularProgress size='25%' style={{ marginBottom: '32px', marginLeft: '35%' }} /></Grid> : <div />}
            <Grid container spacing={5} columns={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 4 }} width="100%">
                <Box width='100%' />
                {filteredItems.length === 0 && state.itemsLoaded ? (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            No shop items match the current filters.
                            {statusFilter === 'active' && disabledCount > 0
                                ? ` ${disabledCount} disabled item(s) — switch Status to “Disabled only” or “All items”.`
                                : ''}
                        </Alert>
                    </Grid>
                ) : null}
                {
                    filteredItems.map(x =>
                        <Grid item xs={1} key={x.id}>
                            <ShopItemCard {...{ item: x, showEdit: true, previewLanguage: 'en', onStatusChanged: () => fetchData(state.osType) }} />
                        </Grid>)
                }
            </Grid>
        </Stack >
    );
}
